import { Loader2, Tag, X } from "lucide-react";
import { Button, Input, Label } from "@/components/ui";
import { cn, formatCurrency } from "@/lib/utils";
import type { CartPromotion } from "@/stores/cart.store";

interface PromoCodeSectionProps {
  appliedPromotion: CartPromotion | null;
  appliedDiscount: number;
  promoCode: string;
  promoError: string;
  isValidating: boolean;
  currencySymbol: string;
  onChange: (value: string) => void;
  onApply: () => void;
  onRemove: () => void;
}

export function PromoCodeSection({
  appliedPromotion,
  appliedDiscount,
  promoCode,
  promoError,
  isValidating,
  currencySymbol,
  onChange,
  onApply,
  onRemove,
}: PromoCodeSectionProps) {
  const isManualCode = appliedPromotion?.type === "discount_code";

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Tag className="h-4 w-4" />
        Promo Code
      </Label>
      {appliedPromotion && (
        <div
          className={cn(
            "flex items-center justify-between rounded-lg border p-3",
            isManualCode
              ? "border-green-200 bg-green-50"
              : "border-amber-200 bg-amber-50",
          )}
        >
          <div>
            <p
              className={cn(
                "font-medium",
                isManualCode ? "text-green-800" : "text-amber-900",
              )}
            >
              {appliedPromotion.name}
            </p>
            <p
              className={cn(
                "text-sm",
                isManualCode ? "text-green-600" : "text-amber-700",
              )}
            >
              -{formatCurrency(appliedDiscount, currencySymbol)} off
              {appliedPromotion.type === "happy_hour" &&
                " — happy hour applied"}
            </p>
          </div>
          {isManualCode && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="remove-promo"
              onClick={onRemove}
              className="text-green-700 hover:bg-green-100"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {!isManualCode && (
        <div className="flex gap-2">
          <Input
            placeholder="Enter code"
            value={promoCode}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onApply();
              }
            }}
            className="flex-1"
          />
          <Button variant="outline" onClick={onApply} disabled={isValidating}>
            {isValidating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Apply"
            )}
          </Button>
        </div>
      )}

      {promoError && <p className="text-sm text-destructive">{promoError}</p>}
    </div>
  );
}
